import { clsx } from 'clsx';
import { Field, useFormikContext } from 'formik';
import { useState } from 'react';
import styles from './EntityFormGrid.module.scss';
import { Card } from '../../../card/Card';
import type {
  EntityFormType,
  EntityFormGrid as EntityFormGridType
} from '../../interfaces/entity-form';
import { Icon } from '../../../icon/Icon';
import { IconButton } from '../../../button/IconButton/IconButton';
import { ENTITY_FORM_GRID_DATA_IDS } from './entityFormGridDataId';
import { useMemoizedDataIds } from '../../../shared/utils/dataId';
import { PrimitiveTypes } from '../../../shared/interfaces/generics';
import { getTemplateFormGridColumnSpan } from '../../../template-form/utils/templateFormGridUtil';

export function EntityFormGrid({
  grid,
  entity,
  formDisabled
}: {
  grid: EntityFormGridType;
  entity: EntityFormType;
  formDisabled: boolean;
}) {
  const [showField, setShowFields] = useState(!grid.props.isCollapsed);
  const { values } = useFormikContext();

  const dataIds = useMemoizedDataIds(ENTITY_FORM_GRID_DATA_IDS, grid.id);

  const toggleFields = () => {
    setShowFields(prevState => !prevState);
  };

  return (
    <Card className={styles.formGridContainer} dataId={dataIds.container}>
      {grid.props.title ? (
        <div className={clsx('flex-col w-100', showField && 'mb-5')}>
          <div className='center-flex-row w-100'>
            {grid.props.isCollapsible ? (
              <IconButton
                dataId={dataIds.toggle}
                onClick={toggleFields}
                className='mr-1'
                label={showField ? 'Collapse' : 'Expand'}
              >
                <Icon
                  dataId={dataIds.toggle}
                  name={showField ? 'dropdown-arrow' : 'right-arrow'}
                  size={2}
                />
              </IconButton>
            ) : null}

            <span data-id={dataIds.title} className='text-ellipsis text-bold center-flex-row'>
              {grid.props.title}
            </span>
          </div>

          {grid.props.subTitle ? (
            <span
              data-id={dataIds.subtitle}
              className={clsx('text-ellipsis text-secondary', styles.gridSubtitle)}
              dangerouslySetInnerHTML={{ __html: grid.props.subTitle }}
            />
          ) : null}
        </div>
      ) : null}

      <div data-id={dataIds.fields} className={clsx(styles.formGrid, !showField && styles.hidden)}>
        {grid.fields.map(field => {
          const shouldRenderField = field.renderExpression
            ? field.renderExpression.evaluate(values as Record<string, PrimitiveTypes>, key => key)
            : true;

          if (!shouldRenderField) {
            return null;
          }

          return (
            <div
              key={field.props.name}
              data-id={dataIds.fieldContainerL1}
              className={clsx(
                field.rowBreak
                  ? 'd-grid grid-template-columns-20 grid-column-span-20'
                  : getTemplateFormGridColumnSpan(field.size)
              )}
            >
              <div
                data-id={dataIds.fieldContainerL2}
                className={field.rowBreak ? getTemplateFormGridColumnSpan(field.size) : 'w-100'}
              >
                <Field
                  {...field.props}
                  entity={entity}
                  formDisabled={formDisabled}
                  name={field.props.name}
                  component={field.component}
                />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
